import React, { useState } from "react";

import { ToggleButton, Stack, Typography, Box, SxProps } from "@mui/material";

interface Option {
  label: string;
  value: string;
}

export interface SettingSliderProps {
  disabled: boolean;
  options: Option[];
  selected: Option["value"];
  onChange: (value: Option["value"]) => void;
  sx?: SxProps;
}

const COLORS = {
  primary: {
    on: "shades.primary.500",
    off: "shades.neutral.200",
  },
  secondary: {
    on: "shades.primary.200",
    off: "shades.neutral.100",
  },
};

const getSliderColor = (
  type: "primary" | "secondary",
  elementIndex: number,
  currentOptionIndex: number,
  hoveredIndex: number,
  element: "button" | "connector" | "border"
) => {
  if (hoveredIndex > -1) {
    if (elementIndex <= hoveredIndex) {
      if (
        elementIndex === hoveredIndex &&
        element !== "border" &&
        type === "primary"
      ) {
        return "#FFF";
      }

      if (element === "connector") {
        return elementIndex <= hoveredIndex - 1
          ? COLORS[type].on
          : COLORS[type].off;
      }

      return COLORS[type].on;
    }

    return COLORS[type].off;
  }

  if (element !== "connector" && elementIndex <= currentOptionIndex) {
    return COLORS[type].on;
  }

  if (element === "connector" && elementIndex <= currentOptionIndex - 1) {
    return COLORS[type].on;
  }

  return COLORS[type].off;
};

const SettingsSlider = ({
  disabled,
  options,
  onChange,
  selected,
  sx,
}: SettingSliderProps) => {
  const selectedIndex = options.findIndex(
    (option) => option.value === selected
  );

  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  const [currentOptionIndex, setCurrentOptionIndex] =
    useState<number>(selectedIndex);

  const handleClick = (value: Option["value"], index: number) => () => {
    if (disabled) return;

    onChange(value);
    setCurrentOptionIndex(index);
  };

  return (
    <Stack direction="row" sx={{ ...sx }} justifyContent="center">
      {options.map((option, i: number) => (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="center"
          sx={{
            cursor: disabled ? "default" : "pointer",
            pointerEvents: disabled ? "none" : undefined,
            opacity: disabled ? 0.5 : 1,
          }}
          key={option.value}
          onClick={handleClick(option.value, i)}
          onMouseEnter={() => setHoveredIndex(i)}
          onMouseLeave={() => setHoveredIndex(-1)}
        >
          <Stack direction="column">
            <ToggleButton
              sx={{
                p: 1,
                backgroundColor: getSliderColor(
                  "primary",
                  i,
                  currentOptionIndex,
                  hoveredIndex,
                  "button"
                ),
                border: "2px solid",
                borderColor: getSliderColor(
                  "primary",
                  i,
                  currentOptionIndex,
                  hoveredIndex,
                  "border"
                ),
                borderRadius: "50%",
                transition: "background 0.1s ease",
              }}
              value={option.value}
            />
            <Typography
              variant="labelMed"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "20px",
                textAlign: "center",
                textOverflow: "visible",
                mt: 1,
              }}
            >
              {option.label}
            </Typography>
          </Stack>
          {i < options.length - 1 && (
            <Stack
              justifyContent="center"
              alignItems="center"
              sx={{ height: "20px" }}
            >
              <Box
                sx={{
                  width: "54px",
                  height: "6px",
                  backgroundColor: getSliderColor(
                    "secondary",
                    i,
                    currentOptionIndex,
                    hoveredIndex,
                    "connector"
                  ),
                  transition: "background 0.1s ease",
                }}
              />
            </Stack>
          )}
        </Stack>
      ))}
    </Stack>
  );
};

export default SettingsSlider;
